// lib
import { Fragment, useContext, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

// components
import TransitionPanel from '@aytm/app/stats/components/transition-panel/TransitionPanel'

import FiltersRowQuestion from '@aytm/app/stats/page/sidebar/filters/rows/FiltersRowQuestion'
import FiltersRowQuestionToggle from '@aytm/app/stats/page/sidebar/filters/rows/FiltersRowQuestionToggle'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersGroupQuestions = ({ group, getFilterValues, getFilterOptions }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const [collapsed, setCollapsed] = useState(
    Object.keys(sidebarState.filters || {})
      .filter(key => {
        const regexp = new RegExp(`^${group.key}-\\S+$`, 'i')
        return regexp.test(key)
      })
      .reduce((acc, cur) => {
        const regexp = new RegExp(`^${group.key}-(\\S+)$`, 'i')
        acc[cur.match(regexp)[1]] = sidebarState.filters[cur]
        return acc
      }, {})
  )

  const options = useMemo(
    () =>
      group.values.reduce((acc, cur) => {
        if (Object.hasOwn(cur, 'parentId') && Object.hasOwn(acc, cur.parentId)) {
          acc[cur.parentId].values.push(cur)
        } else {
          acc[cur.id] = { values: [] }
          acc[cur.id].filter = cur
        }
        return acc
      }, {}),
    [group.values]
  )

  const onToggle = id => {
    const newCollapsed = JSON.parse(JSON.stringify(collapsed))
    const optionCollapsed = !Object.hasOwn(newCollapsed, id) || newCollapsed[id]
    newCollapsed[id] = !optionCollapsed
    setCollapsed(newCollapsed)

    sidebarDispatch({
      type: 'UPDATE_STATE',
      key: 'filters',
      state: {
        [`${group.key}-${id}`]: newCollapsed[id]
      }
    })
  }

  useEffect(
    () =>
      setCollapsed(
        Object.keys(sidebarState.filters || {})
          .filter(key => {
            const regexp = new RegExp(`^${group.key}-\\S+$`, 'i')
            return regexp.test(key)
          })
          .reduce((acc, cur) => {
            const regexp = new RegExp(`^${group.key}-(\\S+)$`, 'i')
            acc[cur.match(regexp)[1]] = sidebarState.filters[cur]
            return acc
          }, {})
      ),
    [group.key, sidebarState.filters]
  )

  return (
    <>
      {Object.values(options).map(option => (
        <Fragment key={option.filter.subkey ? `${option.filter.key}${option.filter.subkey}` : option.filter.key}>
          {0 < option.values.length ? (
            option.filter.disabled ? (
              <>
                <FiltersRowQuestionToggle
                  group={group}
                  filter={option.filter}
                  filterValues={option.values}
                  collapsed={!Object.hasOwn(collapsed, option.filter.id) || collapsed[option.filter.id]}
                  onToggle={onToggle}
                />
                <TransitionPanel
                  className={style['filter-panel']}
                  in={Object.hasOwn(collapsed, option.filter.id) && !collapsed[option.filter.id]}
                >
                  {status =>
                    'exited' === status
                      ? null
                      : option.values.map(filter => (
                          <FiltersRowQuestion
                            key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
                            group={group}
                            filter={filter}
                            filterValues={getFilterValues(filter)}
                            filterOptions={getFilterOptions(filter)}
                          />
                        ))
                  }
                </TransitionPanel>
              </>
            ) : (
              <>
                <FiltersRowQuestion
                  key={option.filter.subkey ? `${option.filter.key}${option.filter.subkey}` : option.filter.key}
                  group={group}
                  filter={option.filter}
                  filterValues={getFilterValues(option.filter)}
                  filterOptions={getFilterOptions(option.filter)}
                />
                <div className={style['filter-panel']}>
                  {option.values.map(filter => (
                    <FiltersRowQuestion
                      key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
                      group={group}
                      filter={filter}
                      filterValues={getFilterValues(filter)}
                      filterOptions={getFilterOptions(filter)}
                    />
                  ))}
                </div>
              </>
            )
          ) : (
            <FiltersRowQuestion
              key={option.filter.subkey ? `${option.filter.key}${option.filter.subkey}` : option.filter.key}
              group={group}
              filter={option.filter}
              filterValues={getFilterValues(option.filter)}
              filterOptions={getFilterOptions(option.filter)}
            />
          )}
        </Fragment>
      ))}
    </>
  )
}

FiltersGroupQuestions.propTypes = {
  group: PropTypes.object,
  getFilterValues: PropTypes.func,
  getFilterOptions: PropTypes.func
}

FiltersGroupQuestions.displayName = 'FiltersGroupQuestions'

export default FiltersGroupQuestions
